/**
 * Cuándo pasó algo, contado como lo contaría una persona.
 *
 * Existe para una pregunta concreta del panel: además de para cuándo es el
 * turno, ¿cuándo lo pidieron? Son dos datos distintos y los dos importan. Un
 * turno de mañana a las 9 reservado hace tres semanas y uno reservado hace diez
 * minutos se ven idénticos en la agenda, y no son lo mismo: el segundo puede ser
 * alguien que acaba de llamar, o el que llegó justo cuando se liberó un lugar, o
 * la reserva que hay que confirmar antes de cerrar. Sin la fecha de alta, para
 * saber si hay algo nuevo hay que acordarse de cómo estaba la lista antes.
 *
 * Se cuenta en relativo y no en fecha exacta porque la pregunta que uno se hace
 * mirando el panel es "¿esto es nuevo?", no "¿qué día fue?". "Hace 10 minutos"
 * se responde solo; "04/08 15:32" hay que restarlo mentalmente contra la hora
 * que es. La fecha exacta igual está, en el `title` del elemento, para cuando
 * hace falta el dato preciso.
 *
 * A partir de la semana se invierte: "hace 23 días" ya no dice nada útil y la
 * fecha sí ubica. Ahí se muestra el día.
 */

const RELATIVO = new Intl.RelativeTimeFormat('es-AR', { numeric: 'auto' });
const DIA_Y_MES = new Intl.DateTimeFormat('es-AR', { day: 'numeric', month: 'long' });
const FECHA_COMPLETA = new Intl.DateTimeFormat('es-AR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const MINUTO = 60 * 1000;
const HORA = 60 * MINUTO;
const DIA = 24 * HORA;

/**
 * "recién", "hace 5 minutos", "ayer", "el 12 de junio".
 *
 * Devuelve `null` —y no un texto de relleno— si no hay fecha o no se entiende.
 * Las reservas anteriores a que se guardara `createdAt` no tienen ninguna, y
 * mostrar "hace 56 años" (que es lo que da interpretar un `undefined` como
 * cero) sería peor que no mostrar nada.
 *
 * El instante actual entra por parámetro, igual que en los módulos del backend:
 * es lo que hace que esto se pueda razonar sin depender de qué hora es.
 */
export function haceCuanto(instante, ahora = Date.now()) {
  if (!instante) return null;
  const cuando = typeof instante === 'number' ? instante : Date.parse(instante);
  if (!Number.isFinite(cuando)) return null;

  const transcurrido = ahora - cuando;

  // Una fecha futura es un reloj desfasado o un dato mal guardado, no algo que
  // haya que explicarle a nadie con "dentro de 3 horas" en la lista de turnos.
  if (transcurrido < 0) return 'recién';

  if (transcurrido < MINUTO) return 'recién';
  if (transcurrido < HORA) return RELATIVO.format(-Math.floor(transcurrido / MINUTO), 'minute');
  if (transcurrido < DIA) return RELATIVO.format(-Math.floor(transcurrido / HORA), 'hour');
  if (transcurrido < 7 * DIA) return RELATIVO.format(-Math.floor(transcurrido / DIA), 'day');

  return `el ${DIA_Y_MES.format(cuando)}`;
}

/** La fecha entera, para el `title`. `null` con los mismos criterios. */
export function fechaExacta(instante) {
  if (!instante) return null;
  const cuando = typeof instante === 'number' ? instante : Date.parse(instante);
  if (!Number.isFinite(cuando)) return null;
  return FECHA_COMPLETA.format(cuando);
}

/**
 * Cuántos minutos faltan para un vencimiento. Negativo si ya pasó.
 *
 * Es para la retención de los turnos que están esperando el pago de la seña: el
 * backend estampa `holdExpiresAt` al crearlos y acá sólo se lee ese sello. La
 * regla —cuánto dura, qué estados retienen— vive en el backend
 * (backend/src/reservas.js) y no se repite de este lado; lo único que viaja es
 * la fecha ya calculada, como pasa con `freeTrialEndDate`.
 *
 * `null` si no hay vencimiento, que es el caso de todo turno firme.
 */
export function minutosHasta(instante, ahora = Date.now()) {
  if (!instante) return null;
  const cuando = typeof instante === 'number' ? instante : Date.parse(instante);
  if (!Number.isFinite(cuando)) return null;
  return Math.ceil((cuando - ahora) / MINUTO);
}

/** ¿La retención sigue viva? Sin sello se considera que sí, como el backend. */
export function retencionVigente(instante, ahora = Date.now()) {
  const minutos = minutosHasta(instante, ahora);
  if (minutos === null) return true;
  return minutos > 0;
}

/* Acá estaba `esReservaReciente`, que decidía el cartel de "Nuevo" del panel
 * con una ventana fija de 12 horas desde que se reservó. Se fue con el cartel:
 * el comentario decía "lo que llegó desde la última vez que el negocio miró el
 * panel", pero eso no es lo que medía —el tiempo transcurrido no sabe si ya lo
 * viste—, y en un negocio con movimiento marcaba todas las filas.
 *
 * La pregunta la contesta ahora lib/novedades.js, que sí recuerda la última
 * visita. No queda una segunda versión dando vueltas a propósito: dos nociones
 * de "reciente" en dos archivos es cómo terminan marcando cosas distintas. */
